import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Check, X, Clock, Mail, MapPin, Building, CreditCard, FileText, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AffiliateManagement = () => {
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(false);

  useEffect(() => {
    fetchAffiliates();
  }, []);

  const fetchAffiliates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/superadmin/affiliates/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAffiliates(response.data);
    } catch (error) {
      console.error('Error fetching affiliates:', error);
      toast.error('Fehler beim Laden der Affiliates');
    } finally {
      setLoading(false);
    }
  };

  const approveAffiliate = async (affiliateId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/superadmin/affiliates/${affiliateId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Affiliate erfolgreich freigeschaltet!');
      fetchAffiliates();
    } catch (error) {
      console.error('Error approving affiliate:', error);
      toast.error('Fehler beim Freischalten');
    }
  };

  const rejectAffiliate = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Bitte Ablehnungsgrund angeben');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/superadmin/affiliates/${selectedAffiliate.id}/reject`,
        { reason: rejectionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Affiliate abgelehnt');
      setShowRejectDialog(false);
      setRejectionReason('');
      setSelectedAffiliate(null);
      fetchAffiliates();
    } catch (error) {
      console.error('Error rejecting affiliate:', error);
      toast.error('Fehler beim Ablehnen');
    }
  };

  const filteredAffiliates = affiliates.filter(a => {
    if (filter === 'all') return true;
    return a.status === filter;
  });

  const getStatusBadge = (status) => {
    const badges = {
      pending: { label: 'Ausstehend', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
      active: { label: 'Aktiv', color: 'bg-green-100 text-green-700', icon: Check },
      rejected: { label: 'Abgelehnt', color: 'bg-red-100 text-red-700', icon: X },
      blocked: { label: 'Blockiert', color: 'bg-gray-100 text-gray-700', icon: AlertCircle }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="h-3 w-3" />
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#0077FF]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Affiliate-Verwaltung</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">Verwalten Sie Partner-Registrierungen und Provisionen</p>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {[
            { key: 'pending', label: 'Ausstehend', count: affiliates.filter(a => a.status === 'pending').length },
            { key: 'active', label: 'Aktiv', count: affiliates.filter(a => a.status === 'active').length },
            { key: 'rejected', label: 'Abgelehnt', count: affiliates.filter(a => a.status === 'rejected').length },
            { key: 'all', label: 'Alle', count: affiliates.length }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                filter === tab.key
                  ? 'bg-[#0077FF] text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {/* Affiliates List */}
        <div className="space-y-4">
          {filteredAffiliates.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-gray-500">Keine Affiliates gefunden</p>
              </CardContent>
            </Card>
          ) : (
            filteredAffiliates.map(affiliate => (
              <Card key={affiliate.id}>
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                            {affiliate.first_name} {affiliate.last_name}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{affiliate.email}</p>
                          <div className="mt-2">
                            {getStatusBadge(affiliate.status)}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                          <div>
                            <div className="font-medium text-gray-700 dark:text-gray-300">Adresse</div>
                            <div className="text-gray-600 dark:text-gray-400">
                              {affiliate.street} {affiliate.house_number}, {affiliate.postal_code} {affiliate.city}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-start gap-2">
                          <FileText className="h-4 w-4 text-gray-400 mt-0.5" />
                          <div>
                            <div className="font-medium text-gray-700 dark:text-gray-300">Steuerstatus</div>
                            <div className="text-gray-600 dark:text-gray-400">
                              {affiliate.tax_status === 'small_business' ? 'Kleinunternehmer' : 'Gewerblich'}
                              {affiliate.vat_id && <div className="text-xs">USt-IdNr: {affiliate.vat_id}</div>}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-start gap-2">
                          <CreditCard className="h-4 w-4 text-gray-400 mt-0.5" />
                          <div>
                            <div className="font-medium text-gray-700 dark:text-gray-300">Auszahlung</div>
                            <div className="text-gray-600 dark:text-gray-400">
                              {affiliate.payout_method === 'manual' ? 'SEPA' : 'PayPal'}
                              {affiliate.iban && <div className="text-xs font-mono">{affiliate.iban}</div>}
                            </div>
                          </div>
                        </div>

                        {affiliate.company_name && (
                          <div className="flex items-start gap-2">
                            <Building className="h-4 w-4 text-gray-400 mt-0.5" />
                            <div>
                              <div className="font-medium text-gray-700 dark:text-gray-300">Firma</div>
                              <div className="text-gray-600 dark:text-gray-400">{affiliate.company_name}</div>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="mt-4 pt-4 border-t">
                        <div className="text-xs text-gray-500">
                          Registriert: {new Date(affiliate.created_at).toLocaleDateString('de-DE', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </div>
                        {affiliate.admin_rejected_reason && (
                          <div className="mt-2 text-xs text-red-600">
                            Ablehnungsgrund: {affiliate.admin_rejected_reason}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    {affiliate.status === 'pending' && (
                      <div className="flex flex-col gap-2 lg:w-48">
                        <Button
                          onClick={() => approveAffiliate(affiliate.id)}
                          className="w-full bg-green-600 hover:bg-green-700 text-white"
                        >
                          <Check className="h-4 w-4 mr-2" />
                          Freischalten
                        </Button>
                        <Button
                          onClick={() => {
                            setSelectedAffiliate(affiliate);
                            setShowRejectDialog(true);
                          }}
                          variant="outline"
                          className="w-full text-red-600 border-red-300 hover:bg-red-50"
                        >
                          <X className="h-4 w-4 mr-2" />
                          Ablehnen
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Reject Dialog */}
        {showRejectDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-900 rounded-lg p-6 max-w-md w-full">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Affiliate ablehnen</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Affiliate: {selectedAffiliate?.first_name} {selectedAffiliate?.last_name}
              </p>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#0077FF] focus:border-transparent"
                rows={4}
                placeholder="Bitte geben Sie einen Ablehnungsgrund an..."
              />
              <div className="flex gap-2 mt-4">
                <Button
                  onClick={() => {
                    setShowRejectDialog(false);
                    setRejectionReason('');
                    setSelectedAffiliate(null);
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={rejectAffiliate}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  Ablehnen
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AffiliateManagement;